import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Divider, Spin } from 'antd';
import type { DataNode } from 'antd/es/tree';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Empty, Form, Input, Modal, Select, Tree } from '@maxtropy/components';
import _ from 'lodash';
import { EnergyUnitTreeItem, getEnergyUnitTree } from '@/api/energyUnitMapping';
import {
  apiV2EnergyAnalysisListByOuPost,
  V2EnergyAnalysisListByOuPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import styles from '../../index.module.scss';

export type AnalysisUnit = Exclude<V2EnergyAnalysisListByOuPostResponse['list'], undefined>[number];

export interface UnitDataNode extends DataNode {
  label?: string;
  parentId?: string;
}

interface EnergyUnitSelectModalProps {
  open: boolean;
  multiple?: boolean;
  title?: string;
  value?: number[];
  unitGroup: AnalysisUnit[];
  unitGroupByUnitId: Map<number, number>;
  onCancel: () => void;
  onOk: (value: number[]) => void;
  onUnitLabelsChange?: (labels: Map<number, string>) => void;
}

export const formatToNodeData = (data: EnergyUnitTreeItem[]): UnitDataNode[] =>
  data.map(item => ({
    title: item.name,
    label: item.name,
    key: item.energyUnitId,
    disabled: item.disabled,
    parentId: item.parentId,
    children: item.children?.length ? formatToNodeData(item.children) : [],
  }));

export const generateList = (data: UnitDataNode[], list: { key: React.Key; title: string }[]) => {
  data.forEach(node => {
    list.push({ key: node.key, title: node.label ?? String(node.title ?? '') });
    if (node.children) {
      generateList(node.children as UnitDataNode[], list);
    }
  });
};

const getParentKey = (key: React.Key, tree: UnitDataNode[]): React.Key | undefined => {
  for (const node of tree) {
    if (node.children?.some(item => item.key === key)) return node.key;
    if (node.children) {
      const parentKey = getParentKey(key, node.children as UnitDataNode[]);
      if (parentKey) return parentKey;
    }
  }
};

const getParentIdsByNodeId = (data: UnitDataNode[], nodeId: React.Key, path: React.Key[] = []): React.Key[] => {
  for (const node of data) {
    const nextPath = [...path, node.key];
    if (node.key === nodeId) return nextPath;
    if (node.children) {
      const found = getParentIdsByNodeId(node.children as UnitDataNode[], nodeId, nextPath);
      if (found.length) return found;
    }
  }
  return [];
};

const getCheckedKeys = (keys: React.Key[] | { checked?: React.Key[]; halfChecked?: React.Key[] }) =>
  Array.isArray(keys) ? keys : keys.checked ?? [];

const EnergyUnitSelectModal: React.FC<EnergyUnitSelectModalProps> = ({
  open,
  multiple = false,
  title = '关联用能单元',
  value = [],
  unitGroup,
  unitGroupByUnitId,
  onCancel,
  onOk,
  onUnitLabelsChange,
}) => {
  const [form] = Form.useForm();
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>();
  const [searchValue, setSearchValue] = useState('');
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [treeNodeData, setTreeNodeData] = useState<UnitDataNode[]>([]);
  const [dataList, setDataList] = useState<{ key: React.Key; title: string }[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);
  const [innerUnitGroup, setInnerUnitGroup] = useState<AnalysisUnit[]>([]);
  const [loading, setLoading] = useState(false);
  const energyGroupId = Form.useWatch('energyGroupId', form);
  const mergedUnitGroup = unitGroup.length ? unitGroup : innerUnitGroup;

  useEffect(() => {
    if (!open) return;
    setSearchValue('');
    setCheckedKeys(value.map(String));
    if (mergedUnitGroup.length) {
      const checkedUnitGroupId = value.map(id => unitGroupByUnitId.get(id)).find(Boolean);
      form.setFieldValue('energyGroupId', checkedUnitGroupId ?? mergedUnitGroup[0].id);
    }
  }, [form, mergedUnitGroup, open, unitGroupByUnitId, value]);

  useEffect(() => {
    if (!open || unitGroup.length) return;
    apiV2EnergyAnalysisListByOuPost({})
      .then(res => {
        setInnerUnitGroup((res.list ?? []) as AnalysisUnit[]);
      })
      .catch(() => {
        setInnerUnitGroup([]);
      });
  }, [open, unitGroup.length]);

  useEffect(() => {
    if (!energyGroupId) {
      setTreeNodeData([]);
      setDataList([]);
      return;
    }
    setLoading(true);
    getEnergyUnitTree(energyGroupId)
      .then(res => {
        const nodes = formatToNodeData(res.list ?? []);
        const list: { key: React.Key; title: string }[] = [];
        generateList(nodes, list);
        setTreeNodeData(nodes);
        setDataList(list);
        setExpandedKeys(list.map(item => item.key));
        onUnitLabelsChange?.(new Map(list.map(item => [Number(item.key), item.title])));
      })
      .catch(() => {
        setTreeNodeData([]);
        setDataList([]);
      })
      .finally(() => setLoading(false));
  }, [energyGroupId, onUnitLabelsChange]);

  const unitGroupOption = useMemo(
    () => mergedUnitGroup.map(item => ({ label: item.name, value: item.id })).filter(item => item.value),
    [mergedUnitGroup]
  );

  const treeData = useMemo(() => {
    const loop = (data: UnitDataNode[]): DataNode[] =>
      data.map(item => {
        const strTitle = item.label ?? String(item.title ?? '');
        const index = strTitle.indexOf(searchValue);
        const title: ReactNode =
          index > -1 ? (
            <span>
              {strTitle.substring(0, index)}
              <span className={styles.siteTreeSearchValue}>{searchValue}</span>
              {strTitle.slice(index + searchValue.length)}
            </span>
          ) : (
            <span>{strTitle}</span>
          );

        return {
          title,
          key: String(item.key),
          disabled: item.disabled,
          selectable: true,
          children: item.children?.length ? loop(item.children as UnitDataNode[]) : undefined,
        };
      });
    return loop(treeNodeData);
  }, [searchValue, treeNodeData]);

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextSearchValue = event.target.value;
    const nextExpandedKeys = dataList
      .map(item => (item.title.indexOf(nextSearchValue) > -1 ? getParentKey(item.key, treeNodeData) : undefined))
      .filter(Boolean) as React.Key[];
    setExpandedKeys(_.uniq(nextExpandedKeys.map(key => getParentIdsByNodeId(treeNodeData, key)).flat()));
    setSearchValue(nextSearchValue);
    setAutoExpandParent(true);
  };

  const handleCheck = (keys: React.Key[] | { checked?: React.Key[]; halfChecked?: React.Key[] }, info: any) => {
    const nextKeys = getCheckedKeys(keys).map(String);
    if (multiple) {
      setCheckedKeys(nextKeys);
      return;
    }
    setCheckedKeys(info?.node?.key ? [String(info.node.key)] : nextKeys.slice(-1));
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={() => onOk(checkedKeys.map(Number).filter(Boolean))}
      afterClose={() => form.resetFields()}
    >
      <Form form={form}>
        <Form.Item label="用能分析组" name="energyGroupId">
          <Select placeholder="请选择" showSearch optionFilterProp="label" options={unitGroupOption} />
        </Form.Item>
        <Form.Item
          extra={
            treeData.length > 0 && (
              <span>
                <ExclamationCircleOutlined style={{ color: 'var(--warning-color)', marginRight: 4 }} />
                灰色选择框表示无该用能单元权限
              </span>
            )
          }
        >
          <Input.Search placeholder="请输入关键字" onChange={onInputChange} />
          <Divider style={{ borderColor: 'rgba(255,255,255,0.15)' }} />
          <Spin spinning={loading}>
            {treeData.length > 0 ? (
              <div className={styles.treeBox}>
                <Tree
                  checkable
                  checkStrictly
                  checkedKeys={checkedKeys}
                  expandedKeys={expandedKeys}
                  autoExpandParent={autoExpandParent}
                  treeData={treeData}
                  onExpand={keys => {
                    setExpandedKeys(keys);
                    setAutoExpandParent(false);
                  }}
                  onCheck={handleCheck}
                />
              </div>
            ) : (
              <Empty />
            )}
          </Spin>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EnergyUnitSelectModal;
